import { z } from 'zod';

import {
  catalogField,
  type ICredentialApp,
  type ICredentialFieldSpec,
  type TConnector,
  type TConnectorField,
} from '../../entities/credential';

export type TCredentialValues = Record<string, string>;

export interface IFormField extends ICredentialFieldSpec {
  field: TConnectorField;
}

/** The app's fields that the catalog still has, in board order. */
export const formFields = (app: ICredentialApp, connectors: readonly TConnector[]): IFormField[] =>
  app.fields.flatMap((spec) => {
    const field = catalogField(connectors, app.connectorKey, spec.path);
    return field ? [{ ...spec, field }] : [];
  });

const schemaDefault = (connectors: readonly TConnector[], app: ICredentialApp, path: string) => {
  const schema = connectors.find(
    (connector) => connector.key === app.connectorKey && connector.latest,
  )?.credential?.jsonSchema;
  const properties = schema?.properties;
  if (typeof properties !== 'object' || properties === null) return undefined;
  const property = (properties as Record<string, unknown>)[path];
  if (typeof property !== 'object' || property === null) return undefined;
  const value = (property as { default?: unknown }).default;
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
};

export const defaultValues = (
  app: ICredentialApp,
  connectors: readonly TConnector[],
): TCredentialValues => {
  const values: TCredentialValues = { name: '' };
  for (const { path, field } of formFields(app, connectors)) {
    values[path] =
      schemaDefault(connectors, app, path) ??
      (field.kind === 'select' ? (field.options?.[0]?.value ?? '') : '');
  }
  return values;
};

// Which HTTP fields a scheme reads; the others stay hidden and are not sent.
export const httpShows = (scheme: string | undefined, path: string): boolean => {
  if (path === 'username') return scheme === 'basic';
  if (path === 'headerName') return scheme === 'header';
  return true;
};

const required = 'Required';

export const credentialSchema = (app: ICredentialApp, connectors: readonly TConnector[]) => {
  const fields = formFields(app, connectors);
  const shape: Record<string, z.ZodString> = {
    name: z.string().trim().min(1, 'Give it a name you will recognise in a step'),
  };
  for (const { path } of fields) shape[path] = z.string();

  return z.object(shape).superRefine((values, context) => {
    for (const { path, field } of fields) {
      const value = values[path]?.trim() ?? '';
      const shown = app.app !== 'http' || httpShows(values.scheme, path);
      const needed =
        field.required ||
        (app.app === 'http' && path !== 'scheme' && shown) ||
        (app.app === 'openai_compatible' && path === 'baseUrl') ||
        (app.connectorKey === 'ai' && path === 'apiKey');
      if (shown && needed && value === '') {
        context.addIssue({ code: 'custom', path: [path], message: required });
      }
      if (field.kind === 'number' && value !== '' && !/^\d+$/.test(value)) {
        context.addIssue({ code: 'custom', path: [path], message: 'A whole number' });
      }
    }
  });
};

/** The body's `fields`: typed values, hidden or empty ones left out, the preset added. */
export const fieldsOf = (
  app: ICredentialApp,
  connectors: readonly TConnector[],
  values: TCredentialValues,
): Record<string, unknown> => {
  const fields: Record<string, unknown> = { ...app.preset };
  for (const { path, field } of formFields(app, connectors)) {
    const value = values[path]?.trim() ?? '';
    if (value === '') continue;
    if (app.app === 'http' && !httpShows(values.scheme, path)) continue;
    fields[path] = field.kind === 'number' ? Number(value) : value;
  }
  return fields;
};

export const httpTokenLabel = (scheme: string | undefined): string =>
  scheme === 'basic' ? 'Password' : 'Token';

export const httpTokenHint = (scheme: string | undefined, header: string | undefined): string => {
  if (scheme === 'basic') return 'sent as Authorization: Basic …';
  if (scheme === 'header') return `sent as ${header?.trim() || 'the header'}: …`;
  return 'sent as Authorization: Bearer …';
};
